/*
 * Lot 11 : « Pourquoi le tonnage varie ». Décomposition du tonnage d'un circuit (ou du territoire extramuros) sur un mois en
 * causes terrain chiffrées + un résidu non expliqué (jamais caché). Démonstration de mécanique sur collectes SIMULÉES :
 * `index` doit avoir été construit bouton Collecte à 1 (sans collecte, chaque fonction rend un résultat vide).
 *
 * Unité de calcul = « PAV-semaine » : une collecte (COLLECTE ou COLLECTE_IMPOSSIBLE) d'un PAV, rattachée au mois de sa date.
 * Les semaines SANS collecte après la dernière collecte d'un PAV (jusqu'à la dernière date de collecte de l'index) sont
 * ajoutées comme semaines virtuelles à 0 kg : c'est la cause `retiree`.
 *
 * V2, causes par PAV-semaine (priorité, une seule cause) : retiree > condamnee > defaut > nouvelle ; sans cause : residu.
 *   condamnee   : lavage impossible répété (borne condamnée) : les deux derniers passages réels de lavage (date strictement
 *                 antérieure à la collecte) sont IMPOSSIBLE. (La cause « impossibles » de la première version est fusionnée ici :
 *                 ses semaines étaient toujours incluses dans celles de la condamnée.)
 *   defaut      : un MAINT_PREV réel dont un élément « Trappe » / « Porte » est en PROBLEME, jusqu'au prochain MAINT_PREV sans
 *                 problème ou 8 semaines (même mécanique que S11b).
 *   nouvelle    : collecte à moins de 8 semaines de la première collecte du PAV.
 * Contribution d'une PAV-semaine = tonnage réel − référence corrigée (négative = perte ; `nouvelle` : référence 0, donc positive).
 * Référence brute (`ref`) : pour une série de semaines sous cause, moyenne des 4 pesées qui précèdent la première (à défaut, moyenne
 * du PAV le mois précédent) ; pour une semaine sans cause, moyenne du PAV le mois précédent (à défaut, 4 pesées précédentes, sinon 0).
 * Groupe témoin : pour un périmètre et un mois M, PAV sans aucune cause sur M ni sur M−1. `facteurSaison` = taux de remplissage moyen
 * des témoins en M / en M−1 (1 si moins de 10 témoins : « saison non estimable »). La référence d'une semaine sous cause (hors
 * `nouvelle`) est la référence brute × facteurSaison. `saison` (périmètre) = tonnage(M−1) × (facteurSaison − 1).
 * Identité : réel − attendu = saison + Σ contributions des causes terrain + résidu.
 */

const MS_JOUR = 86400000;
const versMs = (iso) => { const [y, m, d] = iso.split('-').map(Number); return Date.UTC(y, m - 1, d); };
const versIso = (ms) => new Date(ms).toISOString().slice(0, 10);
const plusJours = (iso, n) => versIso(versMs(iso) + n * MS_JOUR);
const moyenne = (v) => (v.length > 0 ? v.reduce((a, b) => a + b, 0) / v.length : null);
const REEL = (e) => e.source !== 'simulation';
const EST_COLLECTE = (e) => e.source === 'simulation' && (e.type === 'COLLECTE' || e.type === 'COLLECTE_IMPOSSIBLE');

export const CAUSES = ['retiree', 'condamnee', 'defaut', 'nouvelle'];
export const LIBELLES_CAUSES = {
    retiree: 'Borne retirée',
    condamnee: 'Lavage impossible répété (borne condamnée)',
    defaut: 'Défaut de trappe ou de porte',
    nouvelle: 'Nouvelle borne',
    saison: 'Saison (bornes témoins)',
    residu: 'Non expliqué',
};

const SEMAINES_DEFAUT = 8;
const SEMAINES_NOUVELLE = 8;
const MIN_TEMOINS = 10;
const MIN_SEMAINES_MOIS = 3;
const NB_REFERENCE = 4;
const ELEMENTS_BLOQUANTS = ['TRAPPE', 'PORTE'];

const cleElement = (libelle) => String(libelle || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/\s+/g, ' ').trim();
const estBloquant = (libelle) => ELEMENTS_BLOQUANTS.includes(cleElement(libelle));
export const moisPrecedent = (m, n = 1) => {
    let [y, mo] = m.split('-').map(Number);
    mo -= n;
    while (mo < 1) { mo += 12; y -= 1; }
    return `${y}-${String(mo).padStart(2, '0')}`;
};
const moisSuivant = (m) => moisPrecedent(m, -1);

/** Dernier jour (Y-m-d) du mois Y-m. */
export const finDeMois = (m) => { const [y, mo] = m.split('-').map(Number); return versIso(Date.UTC(y, mo, 0)); };

/** PAV-semaines d'un PAV avec cause, référence et contribution. null si le PAV n'a aucune collecte simulée. */
function analyserPav(index, pav, fin) {
    const evts = index.evenementsParPav.get(pav.id) || [];
    const items = [];
    for (const e of evts) {
        if (!EST_COLLECTE(e)) continue;
        const declasse = e.type === 'COLLECTE_IMPOSSIBLE';
        items.push({pavId: pav.id, patrimoineId: e.patrimoineId ?? pav.patrimoineId, date: e.date, mois: e.date.slice(0, 7), kg: declasse ? 0 : ((e.detail && e.detail.tonnageKg) || 0), taux: !declasse && e.detail && typeof e.detail.tauxRemplissage === 'number' ? e.detail.tauxRemplissage : null, declasse, virtuel: false, cause: null, ref: 0, contribution: 0, depuis: null});
    }
    if (items.length === 0) return null;
    items.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    const derniere = items[items.length - 1];
    for (let d = plusJours(derniere.date, 7); d <= fin; d = plusJours(d, 7)) {
        items.push({pavId: pav.id, patrimoineId: derniere.patrimoineId, date: d, mois: d.slice(0, 7), kg: 0, taux: null, declasse: false, virtuel: true, cause: null, ref: 0, contribution: 0, depuis: null});
    }

    const passages = evts.filter((e) => REEL(e) && (e.type === 'LAVAGE' || e.type === 'IMPOSSIBLE'));
    const maints = evts.filter((e) => REEL(e) && e.type === 'MAINT_PREV');
    let ip = 0; let im = 0;
    let precedent = null; let avantPrecedent = null; let defautFin = null;
    const premiere = items[0].date;
    const limiteNouvelle = plusJours(premiere, SEMAINES_NOUVELLE * 7);
    for (const it of items) {
        while (ip < passages.length && passages[ip].date < it.date) {
            avantPrecedent = precedent;
            precedent = passages[ip].type;
            ip++;
        }
        while (im < maints.length && maints[im].date < it.date) {
            const problemes = (maints[im].elements || []).filter((el) => el.statut === 'PROBLEME');
            if (problemes.some((el) => estBloquant(el.libelle))) defautFin = plusJours(maints[im].date, SEMAINES_DEFAUT * 7);
            else if (problemes.length === 0) defautFin = null;
            im++;
        }
        if (it.virtuel) it.cause = 'retiree';
        else if (precedent === 'IMPOSSIBLE' && avantPrecedent === 'IMPOSSIBLE') it.cause = 'condamnee';
        else if (defautFin !== null && it.date <= defautFin) it.cause = 'defaut';
        else if (it.date < limiteNouvelle) it.cause = 'nouvelle';
    }

    // Références. `pesees` = collectes pesées (ni déclassées ni virtuelles) ; moyenne par mois pour « le mois précédent ».
    const parMois = new Map();
    for (const it of items) if (!it.declasse && !it.virtuel) { if (!parMois.has(it.mois)) parMois.set(it.mois, []); parMois.get(it.mois).push(it.kg); }
    const moisPrec = (m) => { const v = parMois.get(moisPrecedent(m)); return v ? moyenne(v) : null; };
    const quatrePrecedentes = (i) => {
        const v = [];
        for (let j = i - 1; j >= 0 && v.length < NB_REFERENCE; j--) if (!items[j].declasse && !items[j].virtuel) v.push(items[j].kg);
        return moyenne(v);
    };
    let refSerie = null; let debutSerie = null;
    items.forEach((it, i) => {
        const terrain = it.cause !== null && it.cause !== 'nouvelle';
        if (it.cause === 'nouvelle') { it.ref = 0; it.depuis = premiere; refSerie = null; } else if (terrain) {
            if (refSerie === null) { refSerie = quatrePrecedentes(i) ?? moisPrec(it.mois) ?? 0; debutSerie = it.date; }
            it.ref = refSerie; it.depuis = debutSerie;
        } else {
            refSerie = null;
            it.ref = moisPrec(it.mois) ?? quatrePrecedentes(i) ?? 0;
        }
        it.contribution = it.kg - it.ref;
    });
    return items;
}

const cache = new WeakMap();

/** Map<pavId, PAV-semaines> de tout l'index (mémoïsé) ; vide si l'index n'a pas de collecte simulée. */
export function semainesParPav(index) {
    let res = cache.get(index);
    if (res) return res;
    let fin = null;
    for (const evts of index.evenementsParPav.values()) {
        for (let i = evts.length - 1; i >= 0; i--) if (EST_COLLECTE(evts[i])) { if (fin === null || evts[i].date > fin) fin = evts[i].date; break; }
    }
    res = new Map();
    if (fin !== null) {
        for (const pav of index.pavs) {
            const items = analyserPav(index, pav, fin);
            if (items) res.set(pav.id, items);
        }
    }
    cache.set(index, res);
    return res;
}

/** Prédicat de portée : `patrimoineId` (un circuit) ou null (territoire extramuros = patrimoines du groupe `extra`). */
function portee(index, patrimoineId) {
    if (patrimoineId !== null && patrimoineId !== undefined) return (it) => it.patrimoineId === patrimoineId;
    const extra = new Set([...index.patrimoineById.values()].filter((p) => p.groupe === 'extra').map((p) => p.id));
    return (it) => extra.has(it.patrimoineId);
}

const lundiDe = (iso) => plusJours(iso, -((new Date(versMs(iso)).getUTCDay() + 6) % 7));

/**
 * V1 : `[{mois, tonnageKg, nbCollectes, nbPav, semaines, partiel}]` (mois croissants ; collectes pesées, déclassés exclus).
 * `semaines` = semaines (lundis) distinctes avec collecte ; `partiel` = moins de 3 semaines de collecte (premier / dernier mois d'un snapshot).
 */
export function tonnageMensuel(index, patrimoineId = null) {
    const dans = portee(index, patrimoineId);
    const mois = new Map();
    for (const items of semainesParPav(index).values()) {
        for (const it of items) {
            if (it.virtuel || it.declasse || !dans(it)) continue;
            let m = mois.get(it.mois);
            if (!m) mois.set(it.mois, (m = {mois: it.mois, tonnageKg: 0, nbCollectes: 0, pavs: new Set(), lundis: new Set()}));
            m.tonnageKg += it.kg; m.nbCollectes++; m.pavs.add(it.pavId); m.lundis.add(lundiDe(it.date));
        }
    }
    return [...mois.values()].sort((a, b) => (a.mois < b.mois ? -1 : 1))
        .map(({pavs, lundis, ...m}) => ({...m, nbPav: pavs.size, semaines: lundis.size, partiel: lundis.size < MIN_SEMAINES_MOIS}));
}

/**
 * V1 : tonnage attendu de `mois` d'après la série `tonnageMensuel`. Même mois N−1 s'il existe avec au moins 80 % du parc du
 * mois (nbPav) ; sinon moyenne des 3 mois précédents présents et non partiels ; sinon null. `{kg, source: 'N-1' | 'moyenne3'}`.
 */
export function attendu(serie, mois) {
    const courant = serie.find((l) => l.mois === mois);
    const parc = courant ? courant.nbPav : null;
    const n1 = serie.find((l) => l.mois === moisPrecedent(mois, 12));
    if (n1 && parc !== null && n1.nbPav >= 0.8 * parc) return {kg: n1.tonnageKg, source: 'N-1'};
    const trois = [1, 2, 3].map((k) => serie.find((l) => l.mois === moisPrecedent(mois, k))).filter((l) => l && !l.partiel);
    if (trois.length === 0) return null;
    return {kg: moyenne(trois.map((l) => l.tonnageKg)), source: 'moyenne3'};
}

/** Les `n` (12) derniers mois ayant au moins une collecte pesée, du plus ancien au plus récent. */
export function moisDisponibles(index, n = 12) {
    const tous = tonnageMensuel(index, null).map((l) => l.mois);
    return tous.slice(-n);
}

const cacheFacteurs = new WeakMap();

/**
 * Groupe témoin : PAV du périmètre présents en `mois` et sans aucune cause sur `mois` ni sur le mois précédent.
 * `facteur` = taux de remplissage moyen des témoins en M / en M−1 ; 1 (`estimable: false`) sous 10 témoins ou sans taux mesurable.
 */
export function facteurSaison(index, patrimoineId, mois) {
    let c = cacheFacteurs.get(index);
    if (!c) cacheFacteurs.set(index, (c = new Map()));
    const cle = `${patrimoineId ?? 'T'}|${mois}`;
    if (c.has(cle)) return c.get(cle);
    const dans = portee(index, patrimoineId);
    const prec = moisPrecedent(mois);
    const tauxM = []; const tauxP = [];
    let nbTemoin = 0;
    for (const items of semainesParPav(index).values()) {
        let present = false; let cause = false;
        const a = []; const b = [];
        for (const it of items) {
            if ((it.mois !== mois && it.mois !== prec) || !dans(it)) continue;
            if (it.cause !== null) { cause = true; break; }
            if (it.mois === mois) present = true;
            if (it.taux !== null) (it.mois === mois ? a : b).push(it.taux);
        }
        if (cause || !present) continue;
        nbTemoin++;
        tauxM.push(...a); tauxP.push(...b);
    }
    const mM = moyenne(tauxM); const mP = moyenne(tauxP);
    const estimable = nbTemoin >= MIN_TEMOINS && mM !== null && mP !== null && mP > 0;
    const res = {facteur: estimable ? mM / mP : 1, estimable, nbTemoin};
    c.set(cle, res);
    return res;
}

/** Référence corrigée de la saison : référence brute × facteur pour une semaine sous cause terrain (hors `nouvelle`, référence 0). */
const refCorrigee = (it, facteur) => (it.cause !== null && it.cause !== 'nouvelle' ? it.ref * facteur : it.ref);

/**
 * V2 + attendu : décomposition de `mois` (Y-m) pour un circuit (`patrimoineId`) ou le territoire (null).
 * `causes[cle] = {kg, nbPav, pavIds}` (contribution Σ réel − référence corrigée) pour les 4 causes terrain.
 * `saison` = tonnage(M−1) × (facteurSaison − 1) ; `saisonEstimable` / `facteurSaison` / `nbTemoin` décrivent le groupe témoin.
 * `attendu`, `ecart`, `residu` sont null si aucun attendu n'est calculable. null si le périmètre n'a aucune donnée ce mois.
 */
export function decomposition(index, patrimoineId, mois) {
    const dans = portee(index, patrimoineId);
    const f = facteurSaison(index, patrimoineId, mois);
    const causes = Object.fromEntries(CAUSES.map((c) => [c, {kg: 0, nbPav: 0, pavIds: []}]));
    let reel = 0; let nb = 0;
    const pavs = new Set();
    for (const items of semainesParPav(index).values()) {
        const vus = new Set();
        for (const it of items) {
            if (it.mois !== mois || !dans(it)) continue;
            nb++; pavs.add(it.pavId);
            reel += it.kg;
            if (it.cause !== null) {
                const c = causes[it.cause];
                c.kg += it.kg - refCorrigee(it, f.facteur);
                if (!vus.has(it.cause)) { vus.add(it.cause); c.nbPav++; c.pavIds.push(it.pavId); }
            }
        }
    }
    if (nb === 0) return null;
    const serie = tonnageMensuel(index, patrimoineId);
    const att = attendu(serie, mois);
    const precedent = serie.find((l) => l.mois === moisPrecedent(mois));
    const saison = (precedent ? precedent.tonnageKg : 0) * (f.facteur - 1);
    const terrain = CAUSES.reduce((sum, c) => sum + causes[c].kg, 0);
    const base = {mois, patrimoineId: patrimoineId ?? null, reel, nbPav: pavs.size, causes, attendu: null, source: null, ecart: null, saison, saisonEstimable: f.estimable, facteurSaison: f.facteur, nbTemoin: f.nbTemoin, residu: null, terrain};
    if (!att) return base;
    const ecart = reel - att.kg;
    return {...base, attendu: att.kg, source: att.source, ecart, residu: ecart - terrain - saison};
}

/**
 * V3 : cascade `[{cle, libelle, kg, nbPav, pavIds?}]` : attendu, causes terrain présentes (ordre de priorité), saison, résidu, réel.
 * `kg` des étapes intermédiaires = contribution signée ; la somme des étapes entre `attendu` et `reel` vaut réel − attendu.
 * null si aucun attendu n'est calculable.
 */
export function cascade(index, patrimoineId, mois) {
    const d = decomposition(index, patrimoineId, mois);
    if (!d || d.attendu === null) return null;
    const etapes = [{cle: 'attendu', libelle: 'Attendu', kg: d.attendu, nbPav: 0, pavIds: []}];
    for (const c of CAUSES) {
        if (d.causes[c].nbPav > 0) etapes.push({cle: c, libelle: LIBELLES_CAUSES[c], kg: d.causes[c].kg, nbPav: d.causes[c].nbPav, pavIds: d.causes[c].pavIds});
    }
    etapes.push({cle: 'saison', libelle: LIBELLES_CAUSES.saison, kg: d.saison, nbPav: 0, pavIds: []});
    etapes.push({cle: 'residu', libelle: LIBELLES_CAUSES.residu, kg: d.residu, nbPav: 0, pavIds: []});
    etapes.push({cle: 'reel', libelle: 'Réel', kg: d.reel, nbPav: 0, pavIds: []});
    return etapes;
}

/** Tableau des mois : `[{mois, partiel, attendu, reel, explique, residu}]` (kg ; explique = causes terrain + saison). */
export function tableauMois(index, patrimoineId, mois) {
    const serie = tonnageMensuel(index, patrimoineId);
    return mois.map((m) => {
        const partiel = !!(serie.find((l) => l.mois === m) || {}).partiel;
        const d = decomposition(index, patrimoineId, m);
        if (!d) return {mois: m, partiel, attendu: null, reel: null, explique: null, residu: null};
        return {mois: m, partiel, attendu: d.attendu, reel: d.reel, explique: d.attendu === null ? null : d.terrain + d.saison, residu: d.residu};
    });
}

/** Seuil de baisse (vs référence) au-delà duquel un PAV sans cause est signalé en bleu. */
export const SEUIL_BAISSE = 0.2;

/**
 * Cause dominante par PAV sur la plage [du, au] (bornes incluses, vides = ouvertes) : `Map<pavId, {cle, kg, depuis, baisse}>`.
 * `cle` = cause terrain de plus grande contribution absolue (égalité : ordre de priorité) ; `kg` = sa contribution sur la plage ;
 * `depuis` = début de la série sous cause. Sans cause terrain : `cle: 'residu'`, `kg` = Σ (réel − référence) de la plage et
 * `baisse` vrai si le réel est inférieur de plus de 20 % à la référence. PAV sans collecte sur la plage : absent.
 */
export function causeDominanteParPav(index, {du = '', au = ''} = {}) {
    const res = new Map();
    for (const [pavId, items] of semainesParPav(index)) {
        const kgs = {}; const depuis = {};
        let reel = 0; let ref = 0; let n = 0;
        for (const it of items) {
            if ((du && it.date < du) || (au && it.date > au)) continue;
            n++; reel += it.kg; ref += it.ref;
            if (it.cause !== null) { kgs[it.cause] = (kgs[it.cause] || 0) + it.kg - refCorrigee(it, facteurSaison(index, it.patrimoineId, it.mois).facteur); if (!depuis[it.cause]) depuis[it.cause] = it.depuis; }
        }
        if (n === 0) continue;
        let choix = null;
        for (const c of CAUSES) if (kgs[c] !== undefined && (choix === null || Math.abs(kgs[c]) > Math.abs(kgs[choix]))) choix = c;
        if (choix !== null) res.set(pavId, {cle: choix, kg: kgs[choix], depuis: depuis[choix], baisse: false});
        else res.set(pavId, {cle: 'residu', kg: reel - ref, depuis: null, baisse: ref > 0 && reel < (1 - SEUIL_BAISSE) * ref});
    }
    return res;
}

/** Clé de couleur du calque Causes (tokens.CAUSE_COULEURS) pour une entrée de `causeDominanteParPav`. */
export const cleCouleurCause = (info) => (info.cle === 'residu' ? (info.baisse ? 'residuBaisse' : 'residu') : info.cle);

const NOMS_CAUSES = {retiree: 'retirée', condamnee: 'lavage impossible répété', defaut: 'défaut de trappe ou de porte', nouvelle: 'nouvelle borne', residu: 'non expliquée'};

/** Ligne de popup : « Cause : condamnée depuis le 12/09 · −38 kg sur la période ». */
export function libelleCause(info) {
    const depuis = info.depuis ? ` depuis le ${info.depuis.slice(8, 10)}/${info.depuis.slice(5, 7)}` : '';
    const kg = Math.round(info.kg);
    return `Cause : ${NOMS_CAUSES[info.cle]}${depuis} · ${kg < 0 ? '−' : kg > 0 ? '+' : ''}${Math.abs(kg)} kg sur la période`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Phrase de constat (calculée, jamais écrite en dur)
// ---------------------------------------------------------------------------------------------------------------------

const MOIS_NOMS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
export const nomMoisLong = (m) => `${MOIS_NOMS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
const nbFr = (x, d = 1) => x.toFixed(d).replace('.', ',');
const signe = (x) => (x < 0 ? '−' : '+');
const tonnes = (kg) => `${nbFr(Math.abs(kg) / 1000)} t`;
const tonnesSignees = (kg) => `${signe(Math.round(kg / 100) * 100)}${tonnes(kg)}`;
const pl = (n, un, plusieurs) => `${n} ${n > 1 ? plusieurs : un}`;

const GROUPES_PHRASE = {
    retiree: (n) => pl(n, 'borne retirée', 'bornes retirées'),
    condamnee: (n) => pl(n, 'borne en lavage impossible répété (condamnée)', 'bornes en lavage impossible répété (condamnées)'),
    defaut: (n) => pl(n, 'défaut de trappe ou de porte', 'défauts de trappe ou de porte'),
    nouvelle: (n) => pl(n, 'nouvelle borne', 'nouvelles bornes'),
};

/** Phrase de constat d'une décomposition (`decomposition(...)`) ; `libelleCircuit` = « le circuit X » ou « le territoire ». */
export function constatVariation(d, libelleCircuit) {
    if (!d) return 'Aucune collecte enregistrée sur ce périmètre ce mois-là : rien à décomposer.';
    const quand = nomMoisLong(d.mois);
    if (d.attendu === null) return `Sur ${libelleCircuit} en ${quand}, le tonnage est de ${tonnes(d.reel)} ; l'historique est insuffisant pour estimer un tonnage attendu.`;
    const pct = d.attendu > 0 ? ` (${signe(d.ecart)}${nbFr(Math.abs(d.ecart) / d.attendu * 100, 0)} %)` : '';
    let phrase = `Sur ${libelleCircuit} en ${quand}, le tonnage est de ${tonnes(d.reel)} contre ${tonnes(d.attendu)} attendues${pct}.`;
    const presentes = CAUSES.filter((c) => d.causes[c].nbPav > 0 && Math.abs(d.causes[c].kg) >= 50);
    if (presentes.length === 0) phrase += ' Aucune cause terrain n\'est détectée.';
    else {
        const parts = presentes.map((c) => `${GROUPES_PHRASE[c](d.causes[c].nbPav)} (${tonnesSignees(d.causes[c].kg)})`);
        phrase += ` Les données terrain expliquent ${tonnes(d.terrain)} : ${parts.join(', ')}.`;
    }
    if (!d.saisonEstimable) phrase += ` Saison non estimable (${pl(d.nbTemoin, 'borne témoin', 'bornes témoins')}, il en faut 10).`;
    else if (Math.abs(d.saison) >= 50) phrase += ` La saison compte pour ${tonnesSignees(d.saison)} (bornes témoins : ×${nbFr(d.facteurSaison, 2)}).`;
    phrase += ` ${tonnes(d.residu)} reste non expliquée.`;
    return phrase;
}

/** Mois suivant, exporté pour les liens de plage. */
export {moisSuivant};
